import { useEffect } from "react";
import { C, GlassCard, Badge, Button } from "@coremonit/design-system";
import { Sparkles, Bell, ShieldCheck, Cpu } from "lucide-react";
import { StepHeader } from "./common";
import type { WizardData } from "../wizardStore";

interface Props { data: WizardData; update: (p: Partial<WizardData>) => void }

export function Step8AI({ data, update }: Props) {
  useEffect(() => {
    if (data.aiRecommendation) return;
    const t = setTimeout(() => {
      update({
        aiRecommendation:
          `Based on the baseline (${data.baseline?.baseline.latencyP95Ms}ms p95, ${data.baseline?.baseline.errorRatePct}% error rate) ` +
          `and dependency graph, CoreMonit suggests the following starter setup for ${data.name || "your service"}: ` +
          `(1) 3 initial alert rules — p95 > 500ms, error rate > 1%, restart count > 3/hr; ` +
          `(2) SLO budget of 99.9% availability over 30 days; ` +
          `(3) enable trace sampling at 100% for first 24h, then 10%; ` +
          `(4) rotate ${data.authMethod} every 90 days.`,
      });
    }, 1200);
    return () => clearTimeout(t);
  }, []); // eslint-disable-line

  return (
    <div>
      <StepHeader title="AI recommendation" description="Given what we've learned about your service, here's a suggested day-1 setup." />
      <GlassCard glow style={{ padding: 22 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
          <div style={{ width: 34, height: 34, borderRadius: 12, background: `linear-gradient(135deg, ${C.blue}, ${C.cyan})`, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Sparkles size={16} color="#fff" />
          </div>
          <div>
            <div style={{ color: C.text, fontSize: 13, fontWeight: 700 }}>CoreMonit AI</div>
            <div style={{ color: C.textMuted, fontSize: 10 }}>Tuned to your baseline · confidence 87%</div>
          </div>
        </div>
        {!data.aiRecommendation ? (
          <div style={{ display: "flex", gap: 6, alignItems: "center", color: C.textMuted, fontSize: 12 }}>
            <Dot /><Dot delay="0.15s" /><Dot delay="0.3s" /> Analyzing…
          </div>
        ) : (
          <>
            <div style={{ color: C.textSub, fontSize: 12.5, lineHeight: 1.7, marginBottom: 16 }}>{data.aiRecommendation}</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 }}>
              <RecTile icon={<Bell size={13} />}        title="3 alert rules"         detail="latency, errors, restarts" color={C.blue} />
              <RecTile icon={<ShieldCheck size={13} />} title="SLO — 99.9% / 30d"    detail="error budget 43.2 min"   color={C.green} />
              <RecTile icon={<Cpu size={13} />}         title="100% → 10% sampling"   detail="24h burn-in"             color={C.purple} />
            </div>
          </>
        )}
      </GlassCard>
    </div>
  );
}

function RecTile({ icon, title, detail, color }: { icon: React.ReactNode; title: string; detail: string; color: string }) {
  return (
    <div style={{ background: C.surfaceHigh, border: `1px solid ${C.border}`, borderRadius: 12, padding: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, color, fontSize: 12, fontWeight: 700 }}>{icon}{title}</div>
      <div style={{ color: C.textMuted, fontSize: 10, marginTop: 4 }}>{detail}</div>
    </div>
  );
}

function Dot({ delay = "0s" }: { delay?: string }) {
  return <span style={{ width: 6, height: 6, borderRadius: "50%", background: C.blue, display: "inline-block", animation: `cm-ai-dot 1.2s ease-in-out infinite`, animationDelay: delay }} />;
}
